<template>
  <div
    :class="{'search-input--active': isDropdownOpen}"
    class="search-input">
    <base-select
      :items="searchResults"
      :should-hide-selected-value="false"
      :value-field="valueField"
      :disabled="disabled"
      @open="openItems"
      @close="closeItems">
      <template #value="slotProps">
        <ui-input
          ref="uiInput"
          v-model="searchValue"
          :title="title"
          :error="error"
          :text-appendix="textAppendix"
          :placeholder="placeholder"
          :disabled="disabled"
          class="search-input__input"
          @focus="$emit('focus')"
          @blur="$emit('blur')"
          @click.native.stop="slotProps.openItems" />
        <arrowhead-icon
          class="search-input__arrow"
          :is-reverse="isDropdownOpen" />
      </template>
      <template #options="slotProps">
        <div class="search-results transition--color-bg">
          <div
            v-for="(item, index) in (isDataListMode ? filteredItems : slotProps.availableItems)"
            :key="`${titleField ? item[titleField] : item}-${index}`"
            class="search-result transition--color"
            @click="selectItem(item); slotProps.closeItems()">
            {{ titleField ? item[titleField] : item }}
          </div>
        </div>
      </template>
    </base-select>
  </div>
</template>

<script>
import UiInput from 'Components/ui/Input/UiInput.vue';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import BaseSelect from 'Components/ui/Select/BaseSelect.vue';

export default {
  name: 'SearchInput',

  components: {
    BaseSelect,
    UiInput,
    ArrowheadIcon,
  },

  props: {
    searchResults: {
      type: Array,
      default: () => [],
    },

    value: {
      type: [String, Object, Number],
      default: '',
    },

    valueField: {
      type: String,
      default: null,
    },

    title: {
      type: String,
      default: '',
    },

    titleField: {
      type: String,
      default: null,
    },

    error: {
      type: [String, Boolean],
      default: null,
    },

    placeholder: {
      type: String,
      default: '',
    },

    isDataListMode: {
      type: Boolean,
      default: false,
    },

    shouldClearEmptyValue: {
      type: Boolean,
      default: true,
    },

    textAppendix: {
      type: Object,
      default: () => ({}),
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    alwaysShowResults: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDropdownOpen: false,
      debounceTimer: null,
      searchValue: '',

      currentValue: null,
    };
  },

  computed: {
    filteredItems() {
      if (this.alwaysShowResults && this.currentValue) {
        const filterItems = [];

        this.searchResults.forEach((item) => {
          const value = this.valueField ? item[this.valueField] : item;
          if (value === this.currentValue) {
            filterItems.unshift(item);
          } else {
            filterItems.push(item);
          }
        });

        return filterItems;
      }

      if (!this.searchValue) {
        return this.searchResults;
      }

      const searchValue = `${this.searchValue}`.toLowerCase();

      if (this.titleField) {
        return this.searchResults.filter((item) => item[this.titleField].toLowerCase().includes(searchValue));
      }

      return this.searchResults.filter((item) => item.toLowerCase().includes(searchValue));
    },
  },

  watch: {
    value(newValue) {
      this.setSearchValue(newValue);
    },

    searchValue: {
      handler() {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = setTimeout(() => {
          if (this.searchValue) {
            if (this.isDropdownOpen) {
              this.emitSearch();
            }
            if (!this.isValueMatched()) {
              this.currentValue = null;
            }
          } else if (this.shouldClearEmptyValue) {
            this.emitValue(this.searchValue);
          }
          clearTimeout(this.debounceTimer);
        }, 300);
      },
    },
  },

  created() {
    this.setSearchValue(this.value);
  },

  methods: {
    isValueMatched() {
      return this.searchValue.toLowerCase() === String(this.value).toLowerCase();
    },

    setSearchValue(value) {
      this.currentValue = value;

      if (!value) {
        this.searchValue = '';
        return;
      }

      if (typeof value === 'object') {
        this.searchValue = this.titleField ? value[this.titleField] : value;
      } else if (typeof this.searchResults[0] === 'object' && this.valueField) {
        const currentItem = this.searchResults.find((item) => item[this.valueField] === value);
        this.searchValue = this.titleField && currentItem ? currentItem[this.titleField] : currentItem;
      } else {
        this.searchValue = value;
      }
    },

    emitSearch() {
      this.$emit('search', this.searchValue);
    },

    emitValue(item) {
      const newValue = item && this.valueField ? item[this.valueField] : item;
      if (newValue === this.currentValue) {
        return;
      }

      this.currentValue = newValue;
      this.$emit('input', newValue);
    },

    selectItem(item) {
      this.emitValue(item);
      this.currentValue = this.valueField ? item[this.valueField] : item;

      if (this.alwaysShowResults) {
        this.searchValue = '';
      } else {
        this.searchValue = this.titleField ? item[this.titleField] : item;
      }
    },

    openItems() {
      if (this.isDropdownOpen || this.disabled) {
        return;
      }
      this.isDropdownOpen = true;
      this.$nextTick(() => {
        this.$refs.uiInput.$refs.input.focus();
      });
      this.emitSearch();
    },

    closeItems(withAutoSelect = false) {
      this.isDropdownOpen = false;

      if (withAutoSelect && this.searchValue && !this.value) {
        const nearestItem = this.filteredItems[0];

        if (nearestItem) {
          this.selectItem(nearestItem);
        }
      } else if (this.value) {
        this.setSearchValue(this.value);
      }

      this.$refs.uiInput.$refs.input.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
  .search-input {
    --border-radius: 3px;
    //--elements-background-color: #{$color--additional-menu};
    //--elements-color: #{$color--basic-light-gray};
    //--elements-color--active: #{$color--basic-white};
    --selector-padding: 4px 20px 4px 20px;
    --elements-padding: 8px 20px 8px 20px;
    --first-last-option-padding: 16px;
    //--input-bg-color: #{$color--additional-menu};
    //--input-padding: 8px 20px;

    &__arrow {
      --shape-color: #{$color--basic-dark-gray};
    }

    &--active {
      .search-input__arrow {
        --shape-color: #{$color--basic-light-gray};
      }
    }
  }

  .search-input__input {
    --background-color: var(--input-bg-color, #{$color--additional-menu});
    --padding: var(--input-padding, 8px 20px);
  }

  .search-input__input /deep/ .ui-input__body {
    margin-right: 22px; // Фиксит проваливание ошибки в стрелку селектора
  }

  .search-input__arrow {
    width: 25px;
    height: 25px;
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
  }

  .search-results {
    --scroll-track-color: var(--elements-background-color, #{$color--additional-menu});
    --scroll-thumb-color: #{$color--basic-superdark-gray};

    position: relative;
    background: var(--elements-background-color, #{$color--additional-menu});
    max-height: 200px;
    overflow-y: auto;
    transform: translateY(calc(-1 * var(--border-radius)));
  }

  .search-result {
    padding: var(--elements-padding);
    cursor: pointer;
    color: var(--elements-color, #{$color--basic-light-gray});
    transition: color .25s cubic-bezier(0, 0, .45, 1);

    &:hover {
      color: var(--elements-color--active, #{$color--basic-white});
    }

    &:first-of-type {
      padding-top: var(--first-last-option-padding);
    }

    &:last-of-type {
      padding-bottom: var(--first-last-option-padding);
      border-radius: 0 0 var(--border-radius) var(--border-radius);
    }
  }

  @media screen and (min-height: 600px) {
    .search-results {
      max-height: 300px;
    }
  }
</style>
